// @ts-check
import React from 'react';
import { Button } from '../Button';
import './ErrorBoundary.css';

/**
 * @typedef {Object} ErrorBoundaryProps
 * @property {React.ReactNode} children
 * @property {React.ReactNode} [fallback] - custom fallback UI; defaults to a card with Reload button
 * @property {(error: Error, info: React.ErrorInfo) => void} [onError] - hook for telemetry
 *
 * @typedef {Object} ErrorBoundaryState
 * @property {Error | null} error
 */

/**
 * ErrorBoundary — catches render errors below it and renders a recovery UI.
 *
 * React's error-boundary contract requires a class component (hooks can't catch
 * render errors). Wrap the App and each major route to keep one broken view
 * from white-screening the entire dashboard.
 *
 * @extends {React.Component<ErrorBoundaryProps, ErrorBoundaryState>}
 */
export class ErrorBoundary extends React.Component {
  /** @param {ErrorBoundaryProps} props */
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  /** @param {Error} error */
  static getDerivedStateFromError(error) {
    return { error };
  }

  /**
   * @param {Error} error
   * @param {React.ErrorInfo} info
   */
  componentDidCatch(error, info) {
    if (this.props.onError) {
      this.props.onError(error, info);
    } else {
      // Default: surface to the console for dev visibility.
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', error, info);
    }
  }

  reload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="error-boundary" role="alert">
          <div className="error-boundary__card">
            <h2 className="error-boundary__title">Something went wrong</h2>
            <p className="error-boundary__message">
              The page hit an unexpected error. Reloading usually clears it.
            </p>
            <details className="error-boundary__details">
              <summary>Technical details</summary>
              <pre className="error-boundary__stack">{this.state.error.message}</pre>
            </details>
            <Button variant="primary" onClick={this.reload}>
              Reload page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
